import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { MuiTypography, OptionsDataType } from "../../../../components";

import {
  CompatibleResult,
  ProgramTemplateAddCompatibleFormKey,
  ProgramTemplateFormKey,
} from "../../modelsAndSchemas/Models";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { CreateCompatibleTemplateWrapper } from "./CompatibleTemplate.style";
import { CreateCompatibleProgramTemplateValidationSchema } from "../../modelsAndSchemas/ValidationSchema";

import { isNotEmpty } from "../../../../helpers/utils";
import {
  toggleLoader,
  toggleToast,
} from "../../../../redux/slices/appState/appStateSlice";
import { CompatibleTemplateList } from "./components/compatibleTemplateList/CompatibleTemplateList";
import { AddCompatibleTemplate } from "./components/addCompatibleTemplate/AddCompatibleTemplate";
import { useCreateCompatibleProgramTemplate } from "../../../../api/services/programTemplate/programTemplateApis";
import { createProgramSiteCoreKeys } from "../../siteCore/SiteCoreKeys";
import { MODULE_PERMISSIONS } from "../../../../helpers/constants";
import PermissionWrapper from "../../../../components/permissionWrapper/PermissionWrapper";

interface CompatibleTemplateProps {
  pageId: string;
  defaultCountryList: OptionsDataType[];
  skippedCountriesList: string[];
  templateId: string;
  isEditTemplate: boolean;
  getCompatibleTemplateList: () => void;
  compatibleTemplateList: CompatibleResult[];
}

export const CompatibleTemplate: FC<CompatibleTemplateProps> = ({
  pageId,
  defaultCountryList,
  skippedCountriesList,
  templateId,
  isEditTemplate,
  getCompatibleTemplateList,
  compatibleTemplateList,
}) => {
  const dispatch = useDispatch();
  const [countryList, setCountryList] = useState<OptionsDataType[]>([]);
  const { commonLabels, commonValidationMessages, fetchLabel } = useSiteCore();

  const {
    mutateAsync: createCompatibleProgramTemplate,
    isPending: isCreateTemplatePending,
    isError: isCreateTemplateError,
    isSuccess: isCreateTemplateSuccess,
  } = useCreateCompatibleProgramTemplate();

  const defaultValues = {
    [ProgramTemplateAddCompatibleFormKey.country]: "",
    [ProgramTemplateAddCompatibleFormKey.compatibleProgramTemplate]: "",
    [ProgramTemplateFormKey.duration]: "",
  };

  const methods = useForm({
    mode: "all",
    defaultValues: defaultValues,
    resolver: yupResolver(
      CreateCompatibleProgramTemplateValidationSchema(commonValidationMessages),
    ),
  });

  const { reset, handleSubmit, getValues } = methods;

  useEffect(() => {
    if (isNotEmpty(defaultCountryList)) {
      setCountryList(
        defaultCountryList
          .filter(
            (country) =>
              !skippedCountriesList.includes(country.value as string),
          )
          .filter(
            (country) =>
              !compatibleTemplateList.some(
                (template) => template.countryCode === country.value,
              ),
          ),
      );
      reset();
    }
  }, [
    JSON.stringify(skippedCountriesList),
    JSON.stringify(defaultCountryList),
    compatibleTemplateList,
  ]);

  const disabledFields = useMemo(() => {
    return skippedCountriesList.length === 0;
  }, [skippedCountriesList]);

  const getLabel = (key: string) => {
    return fetchLabel(createProgramSiteCoreKeys.compatibleTemplateKey, key);
  };

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isCreateTemplatePending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isCreateTemplatePending]);

  useEffect(() => {
    if (isCreateTemplateError) {
      dispatch(
        toggleToast({
          open: true,
          message: commonLabels.someThingWentWrongPleaseTryAgainLater,
          type: "error",
        }),
      );
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isCreateTemplateError]);

  useEffect(() => {
    if (isEditTemplate && templateId) {
      getCompatibleTemplateList();
    }
  }, [templateId]);

  useEffect(() => {
    if (isCreateTemplateSuccess) {
      getCompatibleTemplateList();
      dispatch(
        toggleToast({
          open: true,
          message: getLabel(
            createProgramSiteCoreKeys.compatibleTemplateAddedMessage,
          ),
        }),
      );
      reset();
    }
  }, [isCreateTemplateSuccess]);

  const submitCompatibleTemplate = () => {
    const { country, compatibleProgramTemplate, duration } = getValues();
    createCompatibleProgramTemplate({
      params: {
        compatibleProgramTemplateId: compatibleProgramTemplate,
        duration: Number(duration),
        countryCode: country,
      },
      templateId,
    });
  };

  const handleSubmitValidate = () => {
    handleSubmit(submitCompatibleTemplate)();
  };

  return (
    <CreateCompatibleTemplateWrapper data-testid="compatible-template-form">
      <MuiTypography
        variant="h2Bold"
        className="template-header"
        component="div"
        data-testid="add-compatible-template"
      >
        {getLabel(createProgramSiteCoreKeys.compatibleTemplateSubTitle)}
      </MuiTypography>
      <FormProvider {...methods}>
        <AddCompatibleTemplate
          pageId={pageId}
          countryList={countryList}
          disabledFields={disabledFields}
          templateId={templateId}
          handleAddCompatibleTemplate={handleSubmitValidate}
        />
      </FormProvider>

      <PermissionWrapper
        permission={MODULE_PERMISSIONS.VIEW_COMPATIBLE_TEMPLATES}
      >
        <CompatibleTemplateList
          pageId={pageId}
          rows={compatibleTemplateList}
          templateId={templateId}
          refetchTemplateList={getCompatibleTemplateList}
        />
      </PermissionWrapper>
    </CreateCompatibleTemplateWrapper>
  );
};
